import { AsyncPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, of, Subscription } from 'rxjs';
import { debounceTime, startWith, switchMap, tap } from 'rxjs/operators';
import { Entities } from '../../data-rules-model';
import { MetaSource } from '../../meta-data.service';
import { ICustomPositioningConfig } from '../custom-component-overlay.directive';
import { IProvider } from '../option-items-providers';
import { EntityPickerComponent } from './entity-picker/entity-picker.component';

export interface IOptionItem {
  name: number | string;
  label: string;
}

export interface IEntityItem {
  value: string;
  label: string;
  secondaryDescriptor: string | null;
  description: string;
}

@Component({
  selector: 'app-paired-autocomplete',
  templateUrl: './paired-autocomplete.component.html',
  styleUrls: ['./paired-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgIf,
    EntityPickerComponent,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatAutocomplete,
    MatOption,
    NgForOf,
    AsyncPipe,
    MatTooltipModule
  ]
})
export class PairedAutocompleteComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() selectedFieldControl: FormControl;
  @Input() secondaryEntityControl: FormControl;
  @Input() primaryEntity: Entities;
  @Input() componentId = 'KPPairedAutoCompleteId';
  @Input() placeholder = 'Choose a field...';
  @Input() titleTooltip: string;
  @Input() inputRequired: boolean;
  @Input() dataSourceType: MetaSource = MetaSource.DATA_SOURCE;

  @Input() entitiesOptions: IEntityItem[];
  @Input() transformEntitySelected: Observable<string>;

  private clearInput$: Subscription;
  @Input() clearInputListener: Observable<void>;

  @Input() overlayPositioningConfig: ICustomPositioningConfig;

  @Output() inputCleared = new EventEmitter();

  optionsControl: FormControl;
  filteredOptions$: Observable<IOptionItem[]>;
  searching = false;
  isInputValid = true;

  @ViewChild('optionInput') optionInput: HTMLElement;

  private optionsProvider: IProvider<IOptionItem>;

  readonly displayValue = (name: any): string => {
    const option = this.optionsProvider ? this.optionsProvider.get(name) : null;
    return option ? `${option.label} (${option.name})` : '';
  };

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.optionsControl = new FormControl(
      {disabled: !this.selectedFieldControl || this.selectedFieldControl.disabled},
      this.inputRequired ? Validators.required : null
    );

    this.filteredOptions$ = this.optionsControl.valueChanges.pipe(
      debounceTime(400),
      startWith(''),
      tap(_ => { this.searching = true; this.cdr.markForCheck(); }),
      switchMap(value => this.optionsProvider ? this.optionsProvider.filter(value) : of([])),
      tap(_ => { this.searching = false; this.cdr.markForCheck(); }),
    );

    this.optionsControl.valueChanges.subscribe(() => {
      this.isInputValid = !(this.optionsControl.touched && this.optionsControl.invalid);
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit() {
    if (this.clearInputListener)
      this.clearInput$ = this.clearInputListener.subscribe(() => this.clearInput(this.optionInput, false),
        error => {
          console.error(error);
        });
  }

  setOptionsProvider(optionsProvider: IProvider<IOptionItem>, selOption?: any): void {
    this.optionsProvider = optionsProvider;
    const option = selOption ? optionsProvider.get(selOption) : null;
    this.titleTooltip = option ? option.label : 'Search...';
    this.optionsControl.setValue(selOption ? selOption : '');   // trigger a value change causing the dropdown to be populated
  }

  clearOption(): void {
    this.optionsControl.setValue('');
  }

  onEntitiesDropDownToggle(isOpened: boolean) {
    isOpened ? this.optionsControl.disable() : this.optionsControl.enable();
  }

  optionSelected(e: MatAutocompleteSelectedEvent): void {
    const option = this.optionsProvider ? this.optionsProvider.get(e.option.value) : null;
    this.titleTooltip = option ? option.label : 'Search...';
    this.selectedFieldControl.setValue(e.option.value);
    this.selectedFieldControl.markAsDirty();
  }

  onEscape(event: Event, inputElement: HTMLElement): void {
    if (!this.optionsControl.value) {
      return;
    }

    event.stopPropagation();
    this.clearInput(inputElement, false);
  }

  clearInput(inputElement: HTMLElement, focusInput: boolean): void {
    this.titleTooltip = 'Search...';
    this.optionsControl.setValue('');
    if (focusInput)
      inputElement.focus();
    this.selectedFieldControl.setValue('');
    this.inputCleared.emit();
  }

  forceShowOptionsDropDown(event): void {
    event.target.blur();
    event.target.focus();
  }

  ngOnDestroy() {
    if (this.clearInput$)
      this.clearInput$.unsubscribe();
  }

  disableOptionsAutocomplete(disableOptionsAutocompleteIndicator: boolean) {
    disableOptionsAutocompleteIndicator ?  this.optionsControl.disable() : this.optionsControl.enable();
  }

}
